export interface AdminModel {
  id: number;
  provider: string;
  name: string;
  kind: string;
  enabled: boolean;
  meta: Record<string, unknown>;
}
export type AdminSettings = Record<string, number>;

type Envelope<T> = { ok: true; data: T } | { ok: false; error: string };

// Respect Vite's base path so admin calls work under /ai-visibility-scan in prod.
const base = import.meta.env.BASE_URL.replace(/\/$/, '');

/** Admin login (email + password + TOTP). Returns a session token, or
 *  { totpRequired: true } when the password is correct and a 2FA code is needed. */
export async function adminLogin(email: string, password: string, code?: string): Promise<{ token: string } | { totpRequired: true }> {
  const res = await fetch(`${base}/api/admin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, ...(code ? { code } : {}) }),
  });
  const json = (await res.json()) as
    | { ok: true; data: { token: string } | { totpRequired: true } }
    | { ok: false; error: string };
  if (!json.ok) throw new Error(json.error);
  return json.data;
}

async function adminFetch<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  // Only set Content-Type when there's a body — otherwise Fastify rejects the
  // empty JSON body with 400 (breaks DELETE and body-less POSTs like mark-paid).
  const headers: Record<string, string> = { Authorization: `Bearer ${token}`, ...(init?.headers as Record<string, string> ?? {}) };
  if (init?.body) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${base}${path}`, { ...init, headers });
  const json = (await res.json()) as Envelope<T>;
  if (!json.ok) throw new Error(json.error);
  return json.data;
}

export const getSettings = (token: string) => adminFetch<AdminSettings>('/api/admin/settings', token);

export const putSettings = (token: string, patch: AdminSettings) =>
  adminFetch<{ applied: string[]; settings: AdminSettings }>('/api/admin/settings', token, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });

// ── Pricing (#2) — Pro price + early-access promo, editable in the Config tab ──
export interface AdminPromo { active: boolean; badge: string; note: string }
export interface AdminPricing { monthlyCents: number; annualCents: number; promo: AdminPromo }
export const getPricing = (token: string) => adminFetch<AdminPricing>('/api/admin/pricing', token);
export const putPricing = (token: string, p: AdminPricing) =>
  adminFetch<AdminPricing>('/api/admin/pricing', token, { method: 'PUT', body: JSON.stringify(p) });

export const getModels = (token: string) =>
  adminFetch<{ models: AdminModel[] }>('/api/admin/models', token).then((d) => d.models);

export const toggleModel = (token: string, id: number, enabled: boolean) =>
  adminFetch<{ id: number; enabled: boolean }>(`/api/admin/models/${id}`, token, {
    method: 'PUT',
    body: JSON.stringify({ enabled }),
  });

// ── Commerce dashboard (#3) ───────────────────────────────────────────────
export interface AdminOverview {
  yearMonth: string; customers: number; pro: number; scans: number; probes: number; cost_cents: number;
  runs_today: number; spent_today_cents: number; schedules: number; capUsd: number;
}
export interface AdminCustomer {
  id: string; email: string; name: string | null; created_at: string;
  tier: string; status: string; billing_cycle: string | null; subscription_ends_at: string | null;
  scans: number; probes: number; cost_cents: number;
  /** Lifetime, alongside the month. */
  scans_total: number; probes_total: number; last_run: string | null;
  /** Folded in from the Activity tab so one row tells the whole story. */
  tests: number; logged_seconds: number; sessions: number; last_seen: string | null;
  /** Fair-use overrides — null means "use the global setting" (D-32). */
  probe_per_day: number | null; probe_per_month: number | null; limits_unlimited: boolean; note: string | null;
  /** What the gate has counted so far in the current window. */
  used_today: number; used_month: number;
}
export interface AdminSettingsSnapshot { proProbePerDay: number; proProbePerMonth: number; [k: string]: number }
export interface CustomerLimitPatch {
  tier?: 'free' | 'pro';
  probePerDay?: number | null;
  probePerMonth?: number | null;
  unlimited?: boolean;
  note?: string | null;
  resetCounters?: boolean;
}
export interface AdminInvoice {
  id: string; customer_id: string; email: string; tier: string; billing_cycle: string;
  amount_cents: number; currency: string; status: string; invoice_number: string | null;
  created_at: string; paid_at: string | null;
}

export const getOverview = (token: string) => adminFetch<AdminOverview>('/api/admin/overview', token);
export const getCustomers = (token: string) =>
  adminFetch<{ customers: AdminCustomer[]; settings: AdminSettingsSnapshot }>('/api/admin/customers', token);
export const setCustomerLimits = (token: string, id: string, patch: CustomerLimitPatch) =>
  adminFetch<{ id: number; ok: boolean }>(`/api/admin/customers/${id}/limits`, token, { method: 'POST', body: JSON.stringify(patch) });
export const getInvoices = (token: string) => adminFetch<{ invoices: AdminInvoice[] }>('/api/admin/invoices', token).then((d) => d.invoices);
export const markInvoicePaid = (token: string, id: string) =>
  adminFetch<{ id: number; paid: boolean }>(`/api/admin/invoices/${id}/mark-paid`, token, { method: 'POST' });
export const updateInvoice = (token: string, id: string, fields: Partial<{ invoiceNumber: string | null; amountCents: number; billingCycle: string; status: string }>) =>
  adminFetch<{ id: number; updated: boolean }>(`/api/admin/invoices/${id}/update`, token, { method: 'POST', body: JSON.stringify(fields) });
export const sendInvoice = (token: string, id: string) =>
  adminFetch<{ id: number; sent: boolean; email: string }>(`/api/admin/invoices/${id}/send`, token, { method: 'POST' });
export const deleteInvoice = (token: string, id: string) =>
  adminFetch<{ id: number; deleted: boolean }>(`/api/admin/invoices/${id}`, token, { method: 'DELETE' });
export const deleteCustomer = (token: string, id: string) =>
  adminFetch<{ id: number; deleted: boolean }>(`/api/admin/customers/${id}`, token, { method: 'DELETE' });
export interface ActivityEvent {
  id: string; kind: string; domain: string; url: string; ip: string | null; created_at: string; email: string | null;
}
export interface ActivitySummary {
  email: string; tests: number; last_test: string | null; logged_seconds: number; sessions: number; last_seen: string | null;
}
export const getActivity = (token: string) =>
  adminFetch<{ events: ActivityEvent[]; summary: ActivitySummary[] }>('/api/admin/activity', token);

export interface HistoryRow {
  id: string; createdAt: string; domain: string;
  kind: 'readiness' | 'visibility' | 'footprint';
  readiness: number | null; total: number | null; mentioned: number | null; cited: number | null; knows: boolean | null;
  engine: string | null;
}
export interface HistoryPage { rows: HistoryRow[]; total: number; page: number; pageSize: number; domains: string[] }
export interface EngineStat {
  engine: 'openai' | 'gemini';
  visibility: { mentioned: number; cited: number; total: number; at: string } | null;
  footprint: { mentioned: number; cited: number; total: number; knows: boolean | null; at: string } | null;
}
export const getCustomerScans = (token: string, id: string, opts: { page: number; domain?: string; kind?: string; engine?: string; from?: string; to?: string }) => {
  const p = new URLSearchParams({ page: String(opts.page) });
  if (opts.domain) p.set('domain', opts.domain);
  if (opts.kind) p.set('kind', opts.kind);
  if (opts.engine) p.set('engine', opts.engine);
  if (opts.from) p.set('from', opts.from);
  if (opts.to) p.set('to', opts.to);
  return adminFetch<HistoryPage>(`/api/admin/customers/${id}/scans?${p.toString()}`, token);
};
export const getCustomerEngineComparison = (token: string, id: string, domain?: string) =>
  adminFetch<{ engines: EngineStat[] }>(`/api/admin/customers/${id}/engine-comparison${domain ? `?domain=${encodeURIComponent(domain)}` : ''}`, token).then((d) => d.engines);

// Scan runs (#3b) — admin view.
export interface ScanRun {
  id: string; createdAt: string; domain: string; url: string; engine: string;
  readinessScore: number | null; readinessEngine: string | null;
  visTotal: number | null; visMentioned: number | null; visCited: number | null;
  fpKnows: string | null; fpTotal: number | null; fpMentioned: number | null; fpCited: number | null;
}
export interface ScanRunPage { rows: ScanRun[]; total: number; page: number; pageSize: number; domains: string[] }
export interface ScanRunDetail extends ScanRun { detail: { readiness: unknown; visibility: unknown[]; footprint: unknown } }
export const getCustomerScanRuns = (token: string, id: string, opts: { page: number; domain?: string; engine?: string; from?: string; to?: string }) => {
  const p = new URLSearchParams({ page: String(opts.page) });
  if (opts.domain) p.set('domain', opts.domain);
  if (opts.engine) p.set('engine', opts.engine);
  if (opts.from) p.set('from', opts.from);
  if (opts.to) p.set('to', opts.to);
  return adminFetch<ScanRunPage>(`/api/admin/customers/${id}/scan-runs?${p.toString()}`, token);
};
export const getCustomerScanRunDetail = (token: string, id: string, runId: string) =>
  adminFetch<{ run: ScanRunDetail }>(`/api/admin/customers/${id}/scan-runs/${runId}`, token).then((d) => d.run);
export const getCustomerRunsComparison = (token: string, id: string, domain?: string) =>
  adminFetch<{ runs: ScanRun[] }>(`/api/admin/customers/${id}/runs-comparison${domain ? `?domain=${encodeURIComponent(domain)}` : ''}`, token).then((d) => d.runs);

export const grantPro = (token: string, email: string, months: number) =>
  adminFetch<{ id: number; email: string; granted: boolean }>('/api/admin/customers/grant', token, { method: 'POST', body: JSON.stringify({ email, months }) });
export const revokeCustomer = (token: string, id: string) =>
  adminFetch<{ id: number; revoked: boolean }>(`/api/admin/customers/${id}/revoke`, token, { method: 'POST' });

// ── AI EYE (D-36) ────────────────────────────────────────────────────────────

export interface EyeWidgets {
  readiness: boolean; visibility: boolean; knowsYou: boolean; trend: boolean;
  competitors: boolean; bing: boolean; gsc: boolean; fixit: boolean;
  nextScan: boolean; weeklySentences: boolean;
}
export interface EyeSettings { widgets: EyeWidgets; lang: 'en' | 'nl'; sentences: string[] | null }
export interface EyeSite {
  id: number; siteUrl: string | null; wpVersion: string | null; pluginVersion: string | null;
  scopes: string[]; lastSeenAt: string | null; createdAt: string;
}
export type EyeImportKind = 'bing_ai' | 'gsc_chart' | 'gsc_queries' | 'gsc_pages';
export interface EyeImportResult {
  parsed: number; written: number; skipped: number; notes: string[];
  importId: number | null; duplicateOf: number | null;
}
export interface EyeImport {
  id: number; kind: EyeImportKind; filename: string | null;
  periodFrom: string | null; periodTo: string | null;
  rowsWritten: number; rowsParsed: number; notes: string[];
  createdAt: string; rowsLive: number;
}

export interface EyeDomain { domain: string; runs: number; lastAt: string }

export interface EyeUsageClient {
  customerId: number; email: string; name: string | null;
  views: number; activeDays: number; lastAt: string;
}
export interface EyeUsage {
  days: { day: string; views: number }[];
  screens: { screen: string; views: number; lastAt: string }[];
  totalViews: number; activeDays: number; firstAt: string | null; lastAt: string | null;
}

/** Who opens the plugin, and how often. Zero-view clients are included. */
export const getEyeUsageBoard = (token: string, days = 30) =>
  adminFetch<{ days: number; clients: EyeUsageClient[] }>(`/api/admin/eye/usage?days=${days}`, token);

export const getEyeUsage = (token: string, id: string, days = 90) =>
  adminFetch<EyeUsage>(`/api/admin/clients/${id}/eye/usage?days=${days}`, token);

export interface EyePendingCode {
  id: number; code: string; domain: string | null; allowedSite: string | null;
  expiresAt: string; expired: boolean; createdAt: string;
}

/** Codes issued and not yet used — several sites can be waiting to pair at once. */
export const getEyeCodes = (token: string, id: string) =>
  adminFetch<EyePendingCode[]>(`/api/admin/clients/${id}/eye/codes`, token);

export const deleteEyeCode = (token: string, id: string, codeId: number) =>
  adminFetch<{ deleted: boolean }>(`/api/admin/clients/${id}/eye/codes/${codeId}`, token, { method: 'DELETE' });

/** The domains this client has actually scanned — the only ones a code may name. */
export const getEyeDomains = (token: string, id: string) =>
  adminFetch<EyeDomain[]>(`/api/admin/clients/${id}/eye/domains`, token);

export const createEyeCode = (token: string, id: string, opts: { domain?: string | null; allowedSite?: string | null } = {}) =>
  adminFetch<{ code: string; expiresAt: string; domain: string | null; allowedSite: string | null }>(
    `/api/admin/clients/${id}/eye/code`, token, { method: 'POST', body: JSON.stringify(opts) });
export const getEyeSettings = (token: string, id: string) =>
  adminFetch<EyeSettings>(`/api/admin/clients/${id}/eye/settings`, token);
export const saveEyeSettings = (token: string, id: string, patch: { widgets?: Partial<EyeWidgets>; lang?: 'en' | 'nl'; sentences?: string[] | null }) =>
  adminFetch<EyeSettings>(`/api/admin/clients/${id}/eye/settings`, token, { method: 'POST', body: JSON.stringify(patch) });
export const getEyeSites = (token: string, id: string) =>
  adminFetch<{ tokens: EyeSite[] }>(`/api/admin/clients/${id}/eye/tokens`, token).then((d) => d.tokens);
export const revokeEyeSite = (token: string, id: string, tokenId: number) =>
  adminFetch<{ revoked: boolean }>(`/api/admin/clients/${id}/eye/tokens/${tokenId}`, token, { method: 'DELETE' });
export const importEyeCsv = (token: string, id: string, kind: EyeImportKind, csv: string, periodEnd?: string, filename?: string) =>
  adminFetch<EyeImportResult>(`/api/admin/clients/${id}/imports`, token, {
    method: 'POST',
    body: JSON.stringify({ kind, csv, ...(periodEnd ? { periodEnd } : {}), ...(filename ? { filename } : {}) }),
  });
export const getEyeImports = (token: string, id: string) =>
  adminFetch<{ imports: EyeImport[] }>(`/api/admin/clients/${id}/imports`, token).then((d) => d.imports);
export const deleteEyeImport = (token: string, id: string, importId: number) =>
  adminFetch<{ deleted: boolean; factsRemoved: number }>(`/api/admin/clients/${id}/imports/${importId}`, token, { method: 'DELETE' });
